import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import API from "@/lib/api";
import { Reveal, Eyebrow } from "./Reveal";
import { bnNum } from "@/constants/site";
import { useLang } from "@/lib/lang";

export default function Gallery() {
  const [albums, setAlbums] = useState([]);
  const [year, setYear] = useState("");
  const [lightbox, setLightbox] = useState(-1);
  const { lang, t } = useLang();

  useEffect(() => {
    API.get("/albums")
      .then(({ data }) => {
        setAlbums(data);
        if (data.length) setYear(data[0].year);
      })
      .catch(() => {});
  }, []);

  const album = albums.find((a) => a.year === year);
  const items = album?.items || [];

  const step = (dir) => setLightbox((i) => (i + dir + items.length) % items.length);

  return (
    <section id="gallery" className="py-20 sm:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <Reveal className="text-center max-w-2xl mx-auto">
          <Eyebrow>{t("gal_eyebrow")}</Eyebrow>
          <h2 className="font-bengali font-extrabold text-3xl sm:text-5xl mt-4">
            {t("gal_title_a")} <span className="gold-text">{t("gal_title_b")}</span>
          </h2>
          <p className="font-bengali text-[#D1BBA7] mt-4">{t("gal_sub")}</p>
        </Reveal>

        {albums.length === 0 ? (
          <p className="text-center font-bengali text-[#D1BBA7]/70 mt-12">{t("gal_empty")}</p>
        ) : (
          <>
            <Reveal delay={0.1} className="mt-10 flex flex-wrap gap-2 justify-center">
              {albums.map((a) => (
                <button
                  key={a.year}
                  data-testid={`gallery-year-${a.year}`}
                  onClick={() => setYear(a.year)}
                  className={`rounded-full px-5 py-2 text-sm font-bold transition-all ${
                    year === a.year ? "btn-gold" : "border border-[#F2B134]/25 text-[#D1BBA7] hover:text-[#F2B134]"
                  }`}
                >
                  {lang === "bn" ? bnNum(a.year) : a.year}{" "}
                  <span className="opacity-60">({lang === "bn" ? bnNum(a.count) : a.count})</span>
                </button>
              ))}
            </Reveal>

            <div className="mt-10 columns-2 md:columns-3 gap-4 [column-fill:balance]">
              {items.map((it, i) => (
                <button
                  key={i}
                  data-testid={`gallery-item-${i}`}
                  onClick={() => setLightbox(i)}
                  className="group relative mb-4 block w-full overflow-hidden rounded-2xl border border-[#F2B134]/15 hover:border-[#F2B134]/50 transition-colors spotlight"
                >
                  <img src={it.url} alt={it.name || it.title} loading="lazy" className="w-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#120507] to-transparent p-3 text-left text-xs font-bengali text-[#FDF6EA] opacity-0 group-hover:opacity-100 transition-opacity">
                    {it.name || it.title}
                  </span>
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      <AnimatePresence>
        {lightbox >= 0 && items[lightbox] && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] bg-black/90 grid place-items-center p-4"
            data-testid="gallery-lightbox"
            onClick={() => setLightbox(-1)}
          >
            <button className="absolute top-5 right-5 h-11 w-11 rounded-full border border-[#F2B134]/40 grid place-items-center text-[#FDF6EA]" data-testid="gallery-lightbox-close" onClick={() => setLightbox(-1)}>
              <X size={18} />
            </button>
            <button className="absolute left-4 h-11 w-11 rounded-full border border-[#F2B134]/40 grid place-items-center text-[#FDF6EA]" data-testid="gallery-lightbox-prev" onClick={(e) => { e.stopPropagation(); step(-1); }}>
              <ChevronLeft size={18} />
            </button>
            <motion.img
              key={lightbox}
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              src={items[lightbox].url}
              alt={items[lightbox].name}
              className="max-h-[85vh] max-w-[92vw] object-contain rounded-xl border border-[#F2B134]/30"
              onClick={(e) => e.stopPropagation()}
            />
            <button className="absolute right-4 h-11 w-11 rounded-full border border-[#F2B134]/40 grid place-items-center text-[#FDF6EA]" data-testid="gallery-lightbox-next" onClick={(e) => { e.stopPropagation(); step(1); }}>
              <ChevronRight size={18} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
